import { ReactNode, useMemo, CSSProperties, Ref } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';

type BoxSpacing = 'none' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '13xl';
type BoxOverflow = 'visible' | 'hidden' | 'scroll' | 'auto' | 'clip';

type BoxBorderRadius = 'none' | 'smallest' | 'inset' | 'control' | 'largest' | 'rounded';

type BoxShorthandSpacing = BoxSpacing | { top?: BoxSpacing; right?: BoxSpacing; bottom?: BoxSpacing; left?: BoxSpacing; x?: BoxSpacing; y?: BoxSpacing };

interface Props {
   id?: string;
   tag?: 'div' | 'section';
   grow?: string | number;
   flex?: string;
   content?: 'start' | 'between' | 'center' | 'end' | 'around' | 'stretch';
   direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
   gap?: BoxSpacing;
   items?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
   justify?: 'start' | 'between' | 'center' | 'end' | 'around';
   wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
   position?: 'relative' | 'absolute' | 'fixed' | 'sticky';
   border?: boolean;
   borderColor?: string;
   borderRadius?: BoxBorderRadius | { top: BoxBorderRadius; bottom: BoxBorderRadius };
   top?: string | number;
   left?: string | number;
   bottom?: string | number;
   right?: string | number;
   insetX?: string | number;
   insetY?: string | number;
   inset?: string | number;
   minWidth?: string | number;
   width?: string | number;
   maxWidth?: string | number;
   minHeight?: string | number;
   height?: string | number;
   maxHeight?: string | number;
   wordBreak?: 'normal' | 'all' | 'keep' | 'words';
   overflow?: BoxOverflow;
   overflowX?: BoxOverflow;
   overflowY?: BoxOverflow;
   spacing?: BoxSpacing | BoxShorthandSpacing;
   margin?: BoxSpacing | BoxShorthandSpacing;
   className?: string;
   zIndex?: string;
   background?: string;
   backgroundColor?:
      | 'primary'
      | 'gradient-fade-out-black'
      | 'gradient-fade-out-white'
      | 'gradient-primary'
      | 'scrimmed-alternate'
      | 'scrimmed-default'
      | 'scrimmed-primary'
      | 'scrimmed-theme'
      | 'surface-1'
      | 'surface-2'
      | 'surface-3'
      | 'surface-4'
      | 'surface-default'
      | 'system-danger'
      | 'system-eco'
      | 'system-info'
      | 'system-success'
      | 'system-warning';
   shadow?: boolean;
   enableHoverState?: boolean;
   attributes?: Record<string, string>;
   onClick?: () => void;
   children?: ReactNode | Array<ReactNode>;
   ref?: Ref<HTMLDivElement>;
}

/* istanbul ignore next */
const calculateSpacing = (type: 'padding' | 'margin', spacing?: BoxSpacing | BoxShorthandSpacing) => {
   if (!spacing || typeof spacing === 'object') {
      if (!spacing) return null;
      if (Object.values(spacing).every((value) => !value || value === 'none')) return null;
   }

   const cssVarName = (size: BoxSpacing) => `var(--component-layout-box-spacing-padding-${size.replace('-', '')})`;
   const cssValue = (size: BoxSpacing) => (size.indexOf('-') === 0 ? `calc(${cssVarName(size)} * -1)` : cssVarName(size));

   if (typeof spacing === 'string') return { [type]: cssValue(spacing) };

   return { [type]: `${cssValue(spacing?.y || spacing?.top || 'none')} ${cssValue(spacing?.x || spacing?.right || 'none')} ${cssValue(spacing?.y || spacing?.bottom || 'none')} ${cssValue(spacing?.x || spacing?.left || 'none')}` };
};

const getInset = (value: string | number | undefined, fallbackValue: string | number | undefined) => {
   if (value !== undefined) return value;
   return fallbackValue;
};

export const Box = ({
   id,
   tag = 'div',
   grow,
   flex,
   content,
   direction,
   gap = 'lg',
   items,
   justify,
   wrap,
   position,
   top,
   left,
   bottom,
   right,
   insetX,
   insetY,
   inset,
   minWidth,
   width,
   maxWidth,
   minHeight,
   height,
   maxHeight,
   overflow,
   overflowX,
   overflowY,
   spacing,
   margin,
   wordBreak,
   border,
   borderColor,
   borderRadius,
   className,
   zIndex,
   background,
   backgroundColor,
   shadow,
   enableHoverState,
   attributes = {},
   onClick,
   children,
   ref
}: Props) => {
   const calculatedMargins = useMemo(() => calculateSpacing('margin', margin), [margin]);
   const calculatedPaddings = useMemo(() => calculateSpacing('padding', spacing), [spacing]);

   const cssClasses = classNames(
      'flex',
      content === 'around' && 'content-around',
      content === 'between' && 'content-between',
      content === 'center' && 'content-center',
      content === 'end' && 'content-end',
      content === 'start' && 'content-start',
      content === 'stretch' && 'content-stretch',
      direction === 'column' && 'flex-col',
      direction === 'column-reverse' && 'flex-col-reverse',
      direction === 'row' && 'flex-row',
      direction === 'row-reverse' && 'flex-row-reverse',
      gap === '2xl' && 'gap-component-layout-box-spacing-gap-2xl',
      gap === '2xs' && 'gap-component-layout-box-spacing-gap-2xs',
      gap === '3xl' && 'gap-component-layout-box-spacing-gap-3xl',
      gap === '3xs' && 'gap-component-layout-box-spacing-gap-3xs',
      gap === 'lg' && 'gap-component-layout-box-spacing-gap-lg',
      gap === 'md' && 'gap-component-layout-box-spacing-gap-md',
      gap === 'none' && 'gap-component-layout-box-spacing-gap-none',
      gap === 'sm' && 'gap-component-layout-box-spacing-gap-sm',
      gap === 'xl' && 'gap-component-layout-box-spacing-gap-xl',
      gap === 'xs' && 'gap-component-layout-box-spacing-gap-xs',
      items === 'baseline' && 'items-baseline',
      items === 'center' && 'items-center',
      items === 'end' && 'items-end',
      items === 'start' && 'items-start',
      items === 'stretch' && 'items-stretch',
      justify === 'around' && 'justify-around',
      justify === 'between' && 'justify-between',
      justify === 'center' && 'justify-center',
      justify === 'end' && 'justify-end',
      justify === 'start' && 'justify-start',
      wrap === 'nowrap' && 'flex-nowrap',
      wrap === 'wrap' && 'flex-wrap',
      wrap === 'wrap-reverse' && 'flex-wrap-reverse',
      wordBreak === 'all' && 'break-all',
      wordBreak === 'keep' && 'break-keep',
      wordBreak === 'normal' && 'break-normal',
      wordBreak === 'words' && 'break-words',
      shadow && 'shadow-component-layout-box-box-shadow-default transition-shadow',
      border && 'border-component-layout-box-color-border-default border-component-layout-box-border-width-brand',
      borderRadius === 'smallest' && 'rounded-component-layout-box-border-radius-smallest',
      borderRadius === 'inset' && 'rounded-component-layout-box-border-radius-inset',
      borderRadius === 'control' && 'rounded-component-layout-box-border-radius-control',
      borderRadius === 'largest' && 'rounded-component-layout-box-border-radius-largest',
      borderRadius === 'rounded' && 'rounded-component-layout-box-border-radius-rounded',
      typeof borderRadius === 'object' && borderRadius.top === 'smallest' && 'rounded-t-component-layout-box-border-radius-smallest',
      typeof borderRadius === 'object' && borderRadius.top === 'inset' && 'rounded-t-component-layout-box-border-radius-inset',
      typeof borderRadius === 'object' && borderRadius.top === 'control' && 'rounded-t-component-layout-box-border-radius-control',
      typeof borderRadius === 'object' && borderRadius.top === 'largest' && 'rounded-t-component-layout-box-border-radius-largest',
      typeof borderRadius === 'object' && borderRadius.top === 'rounded' && 'rounded-t-component-layout-box-border-radius-rounded',
      typeof borderRadius === 'object' && borderRadius.bottom === 'smallest' && 'rounded-b-component-layout-box-border-radius-smallest',
      typeof borderRadius === 'object' && borderRadius.bottom === 'inset' && 'rounded-b-component-layout-box-border-radius-inset',
      typeof borderRadius === 'object' && borderRadius.bottom === 'control' && 'rounded-b-component-layout-box-border-radius-control',
      typeof borderRadius === 'object' && borderRadius.bottom === 'largest' && 'rounded-b-component-layout-box-border-radius-largest',
      typeof borderRadius === 'object' && borderRadius.bottom === 'rounded' && 'rounded-b-component-layout-box-border-radius-rounded',
      onClick && 'cursor-pointer',
      shadow && 'shadow-component-layout-box-box-shadow-default',
      enableHoverState && 'hover:shadow-component-layout-box-box-shadow-hover',
      backgroundColor === 'primary' && 'bg-theme-color-primary',
      backgroundColor === 'gradient-fade-out-black' && 'bg-theme-color-overlay-default',
      backgroundColor === 'gradient-fade-out-white' && 'bg-component-surfaces-overlay-container-color-background-gradient-fade-out-light-final',
      backgroundColor === 'gradient-primary' && 'bg-component-surfaces-overlay-container-color-background-gradient-primary',
      backgroundColor === 'scrimmed-alternate' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-dark',
      backgroundColor === 'scrimmed-primary' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-primary',
      backgroundColor === 'scrimmed-theme' && 'bg-component-surfaces-overlay-container-color-background-scrimmed-theme',
      backgroundColor === 'surface-1' && 'bg-theme-color-semantic-on-danger-hard-strong',
      backgroundColor === 'surface-2' && 'bg-theme-color-bg-inset',
      backgroundColor === 'surface-3' && 'bg-theme-color-bg-disabled',
      backgroundColor === 'surface-4' && 'bg-theme-color-border',
      backgroundColor === 'surface-default' && 'bg-theme-color-bg-default',
      backgroundColor === 'system-danger' && 'bg-theme-color-semantic-danger-soft',
      backgroundColor === 'system-eco' && 'bg-theme-color-semantic-eco',
      backgroundColor === 'system-info' && 'bg-theme-color-semantic-info',
      backgroundColor === 'system-success' && 'bg-theme-color-semantic-success',
      backgroundColor === 'system-warning' && 'bg-theme-color-semantic-warning',
      className
   );

   const cssStyles: CSSProperties = {
      flexGrow: grow,
      flex,
      minWidth,
      minHeight,
      width,
      height,
      maxWidth,
      maxHeight,
      overflow,
      overflowX,
      overflowY,
      position,
      inset,
      zIndex,
      background,
      borderColor,
      left: getInset(left, insetX),
      top: getInset(top, insetY),
      right: getInset(right, insetX),
      bottom: getInset(bottom, insetY),
      ...calculatedMargins,
      ...calculatedPaddings
   };

   return tag === 'section' ? (
      <section role="presentation" id={id} data-testid="box-section" ref={ref} className={cssClasses} style={cssStyles} {...dataAttributes(attributes)} onClick={onClick} onKeyDown={onClick}>
         {children}
      </section>
   ) : (
      <div role="presentation" id={id} data-testid="box-div" ref={ref} className={cssClasses} style={cssStyles} {...dataAttributes(attributes)} onClick={onClick} onKeyDown={onClick}>
         {children}
      </div>
   );
};
